import React from 'react';
import {
    Box,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';

import ChartCard from 'c-components/HiddenReport/Components/ChartCard';
import CustomSplitReportPage from 'c-components/HiddenReport/Components/CustomSplitReportPage';
import OneMapPage from 'c-components/HiddenReport/Components/OneMapPage';
import SplitChartWithInsights from 'c-components/HiddenReport/Components/SplitChartWithInsights';
import SplitReportPage from 'c-components/HiddenReport/Components/SplitReportPage';
import TwoChartPage from 'c-components/HiddenReport/Components/TwoChartPage';
import TwoMapPage from 'c-components/HiddenReport/Components/TwoMapPage';
import poweredByUniledLogo from 'public/poweredByUniledLogo.png';
import uniledLogo from 'public/uniledLogo.png';

import type { CustomReportResolvedData, MultiplierRow, SummaryBarRow } from './customReportData';
import { fmtCompact, formatPercent, perfColor } from './customReportDataLib';
import type {
    ChartSpec,
    CustomInsightRowSpec,
    CustomChartMetric,
    CustomGroupByField,
    CustomReportSlideConfig,
    DspSplitChartInsightsSlideConfig,
    GlossarySlideConfig,
    ImpactMultiplierColumnConfig,
    ImpactMultiplierColumnId,
    ImpactMultiplierSectionConfig,
    ImpactMultiplierSlideConfig,
    InsightSpec,
    PerformanceBoxesSlideConfig,
    SingleChartSlideConfig,
    SummaryCoverSlideConfig,
    ThankYouSlideConfig,
} from './customReportTypes';

const DOW_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_MAX_ITEMS = 5;
const X_AXIS_HEIGHT = 78;
const SUMMARY_CARD_KEYS = ['impacts', 'plays', 'time'] as const;
const SUMMARY_CARD_ROW_KEYS = ['performance', 'total', 'expected', 'variance'] as const;

const fmtInt = (n?: number) =>
    typeof n === 'number' && Number.isFinite(n) ? Math.round(n).toLocaleString() : '0';
const fmt2 = (n?: number) =>
    typeof n === 'number' && Number.isFinite(n)
        ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : '0';
const fmtPct = (d: number) => `${Math.round((d ?? 0) * 100)} %`;

const isChronologicalDataset = (dataset: string) =>
    dataset === 'day_of_week' || dataset === 'hour_of_day';

const getChartGroupByFields = (chart: ChartSpec): CustomGroupByField[] => {
    if (chart.groupByFields?.length) return chart.groupByFields;
    if (chart.dataset === 'display_env_format') return ['display_env', 'display_format'];
    if (
        chart.dataset === 'display_owner' ||
        chart.dataset === 'display_env' ||
        chart.dataset === 'display_format' ||
        chart.dataset === 'display_tv_region' ||
        chart.dataset === 'day_of_week' ||
        chart.dataset === 'hour_of_day'
    ) {
        return [chart.dataset];
    }
    return [];
};

const getChartDatasetKey = (chart: ChartSpec) => {
    const groupByFields = getChartGroupByFields(chart);
    return groupByFields.length > 0 ? groupByFields.join('__') : chart.dataset;
};

const getInsightDatasetKey = (insight: InsightSpec | CustomInsightRowSpec) => {
    if ('groupByFields' in insight && insight.groupByFields?.length) {
        return insight.groupByFields.join('__');
    }

    if ('dataset' in insight) {
        return insight.dataset;
    }

    return '';
};

const getMetricKeys = (metric: CustomChartMetric) =>
    metric === 'impacts'
        ? {
              totalKey: 'impacts_total' as const,
              expectedKey: 'impacts_expected' as const,
              totalLegend: 'Total',
              expectedLegend: 'Expected',
          }
        : {
              totalKey: 'plays_total' as const,
              expectedKey: 'plays_expected' as const,
              totalLegend: 'Total',
              expectedLegend: 'Expected',
          };

const getMetricWord = (metric: CustomChartMetric, language: 'GB' | 'US') => {
    if (metric === 'plays') return 'Plays';
    return language === 'GB' ? 'Impacts' : 'Impressions';
};

const resolveUse12hHourFormat = (
    explicit: '12h' | '24h' | undefined,
    globalDefault?: boolean,
): boolean => {
    if (explicit === '12h') return true;
    if (explicit === '24h') return false;
    return globalDefault === true;
};

const formatHour12 = (hour: number): string => {
    const h = ((hour % 24) + 24) % 24;
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
};

const formatHourRange = (start: number, endExclusive: number, use12h: boolean): string => {
    if (use12h) {
        return `${formatHour12(start)} – ${formatHour12(endExclusive)}`;
    }
    return `${String(start).padStart(2, '0')}:00 – ${String(endExclusive).padStart(2, '0')}:00`;
};

const localizeConfigText = (text: string | undefined, language: 'GB' | 'US') => {
    if (!text || language !== 'US') return text;

    // "Media Owner(s)" has to be consumed whole, and the plural has to go first: leave it to
    // the bare Owner(s) rules below and the "Media " is left stranded in front, giving
    // "Media Partners" instead of "Partners".
    return text
        .replace(/\bTV Region\b/g, 'State')
        .replace(/\bMedia Owners\b/g, 'Partners')
        .replace(/\bMedia Owner\b/g, 'Partner')
        .replace(/\bOwners\b/g, 'Partners')
        .replace(/\bOwner\b/g, 'Partner')
        .replace(/\bImpacts\b/g, 'Impressions')
        .replace(/\bImpact\b/g, 'Impression')
        .replace(/\bRegion\b/g, 'State');
};

const localizeConfigNode = (value: React.ReactNode, language: 'GB' | 'US'): React.ReactNode =>
    typeof value === 'string' ? localizeConfigText(value, language) : value;

const resolveSlideBrand = (
    report: CustomReportResolvedData,
    layoutConfig?: { brandVariant?: 'primary' | 'accent' },
) => (layoutConfig?.brandVariant === 'accent' ? report.accentBrand : report.brand);

const resolveFooterBrand = (
    report: CustomReportResolvedData,
    layoutConfig?: { footerBrandVariant?: 'primary' | 'accent' },
) => (layoutConfig?.footerBrandVariant === 'accent' ? report.accentBrand : report.brand);

const getVisibleSummaryCards = (
    slide: SummaryCoverSlideConfig,
    report: CustomReportResolvedData,
) => {
    if (slide.summaryCards?.length) {
        const cardByKey = {
            impacts: report.summaryCards[0],
            plays: report.summaryCards[1],
            time: report.summaryCards[2],
        } as const;

        return slide.summaryCards.map(key => cardByKey[key]).filter(Boolean);
    }

    return report.summaryCards.slice(0, slide.headlineCount ?? 3);
};

const getVisibleSummaryCardRows = (slide: SummaryCoverSlideConfig, preset: string) => {
    if (slide.summaryCardRows?.length) return slide.summaryCardRows;
    return preset === 'dspSummary'
        ? (['total'] as Array<'performance' | 'total' | 'expected' | 'variance'>)
        : [...SUMMARY_CARD_ROW_KEYS];
};

const pickTopBottom = (rows: MultiplierRow[], positive: boolean, count = 5) => {
    const filtered = rows.filter(row =>
        positive ? row.variance_percent > 0 : row.variance_percent < 0,
    );
    return filtered.sort((a, b) => b.dsp_impressions - a.dsp_impressions).slice(0, count);
};

const getRowsForDataset = (
    data: CustomReportResolvedData,
    dataset: string,
    options?: { maxItems?: number; includeZeroValues?: boolean },
) => {
    const rows = data.datasets[dataset] ?? [];
    const includeZeroValues = options?.includeZeroValues ?? false;
    const filteredRows = includeZeroValues ? rows : rows.filter(rowHasAnyMetrics);

    if (
        isChronologicalDataset(dataset) ||
        !options?.maxItems ||
        filteredRows.length <= options.maxItems
    ) {
        return filteredRows;
    }

    const topRows = filteredRows.slice(0, options.maxItems);
    const others = filteredRows.slice(options.maxItems).reduce<SummaryBarRow>(
        (acc, row) => ({
            name: 'Others',
            impacts_total: acc.impacts_total + row.impacts_total,
            impacts_expected: acc.impacts_expected + row.impacts_expected,
            plays_total: acc.plays_total + row.plays_total,
            plays_expected: acc.plays_expected + row.plays_expected,
        }),
        {
            name: 'Others',
            impacts_total: 0,
            impacts_expected: 0,
            plays_total: 0,
            plays_expected: 0,
        },
    );

    return rowHasAnyMetrics(others) ? [...topRows, others] : topRows;
};

const rowHasAnyMetrics = (row: SummaryBarRow) =>
    row.impacts_total !== 0 ||
    row.impacts_expected !== 0 ||
    row.plays_total !== 0 ||
    row.plays_expected !== 0;

const wrapLabel = (value: unknown, max = 23): string[] => {
    const source = String(value ?? '');
    if (!source) return [''];

    const words = source.split(/\s+/);
    const lines: string[] = [];
    let line = '';

    words.forEach(word => {
        if (!line) {
            line = word;
            return;
        }

        if (`${line} ${word}`.length <= max) {
            line = `${line} ${word}`;
            return;
        }

        lines.push(line);
        line = word;
    });

    if (line) lines.push(line);
    return lines;
};

const AngledMultiLineTick: React.FC<{
    x?: number;
    y?: number;
    payload?: { value: unknown };
    fontSize?: number;
}> = ({ x = 0, y = 0, payload, fontSize = 11 }) => {
    const lines = wrapLabel(payload?.value);

    return (
        <g transform={`translate(${x},${y}) rotate(-36)`}>
            <text textAnchor="end" style={{ fontSize }}>
                {lines.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tspan key={index} x={0} dy={index === 0 ? 0 : 12}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const SummaryCoverSlide: React.FC<{
    slide: SummaryCoverSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const preset = slide.preset ?? slide.variant ?? 'linear';
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);
    const visibleSummaryCards = getVisibleSummaryCards(slide, report);
    const visibleSummaryCardRows = getVisibleSummaryCardRows(slide, preset);

    if (preset === 'dspCover') {
        return (
            <CustomSplitReportPage
                logoUrl={report.logoUrl}
                brand={slideBrand}
                footerBrand={footerBrand}
                campaignId={campaignId}
                campaignTitle={report.title}
                splitWidth={slide.layoutConfig?.splitWidth ?? '50%'}
            >
                <Box sx={{ maxWidth: 'calc(var(--split-w))' }}>
                    <Box sx={{ mt: 2, pl: 1 }}>
                        <Typography sx={{ color: 'black', fontSize: '72px', fontWeight: 900 }}>
                            {localizeConfigText(slide.title, report.language) ?? 'Delivery Data'}
                            <br />& Insights
                        </Typography>
                        <Box
                            className="cover-underline"
                            sx={{
                                bgcolor: slideBrand,
                                mt: 2,
                                height: 10,
                                width: 240,
                                borderRadius: 1.5,
                            }}
                        />
                    </Box>
                </Box>
            </CustomSplitReportPage>
        );
    }

    if (preset === 'dspSummary') {
        return (
            <Box
                className="page page--grey"
                sx={{
                    backgroundColor: '#f3f4f6',
                    '@media print': {
                        backgroundColor: '#f3f4f6',
                    },
                }}
            >
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box
                            component="img"
                            src={report.logoUrl}
                            alt="Logo"
                            className="header-logo"
                            sx={{
                                maxHeight: 48,
                                maxWidth: 270,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    </Stack>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, color: slideBrand, mb: 0.5 }}>
                    {report.title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: slideBrand, mb: 3 }}>
                    {localizeConfigText(slide.title, report.language) ?? 'Campaign Summary'}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 8, maxWidth: 820 }}>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            Campaign Live Dates:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 700 }}>{report.liveDates || '—'}</Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            Report Data Range:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 700 }}>
                            {report.reportDates || '—'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            Campaign Number:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800 }}>#{campaignId}</Typography>
                    </Grid>
                </Grid>

                <Typography variant="h4" sx={{ fontWeight: 900, color: slideBrand, mb: 2, mt: 14 }}>
                    Performance Headlines
                </Typography>

                <Grid container spacing={3} className="print-three" sx={{ maxWidth: 1240 }}>
                    {visibleSummaryCards.map(card => (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={card.title}
                            sx={{
                                '@media print': {
                                    width: '33.3333%',
                                    flexBasis: '33.3333%',
                                    maxWidth: '33.3333%',
                                },
                            }}
                        >
                            <Box
                                className="metric-card"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: '#fff',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        color: slideBrand,
                                        mb: 1,
                                        borderBottom: `3px solid ${slideBrand}`,
                                        display: 'inline-block',
                                    }}
                                >
                                    {card.title}
                                </Typography>
                                {visibleSummaryCardRows.includes('total') && (
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="baseline"
                                    >
                                        <Typography sx={{ color: 'text.secondary' }}>
                                            Total
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                            {card.total}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Box className="page-footer" sx={{ px: 2 }}>
                    <Stack direction="row" alignItems="flex-end" gap={2} sx={{ width: 1, pl: 1.5 }}>
                        <Box
                            component="img"
                            src={poweredByUniledLogo}
                            alt="Powered by UniLED"
                            sx={{ maxHeight: 36, objectFit: 'contain', display: 'block' }}
                        />
                        <Box
                            sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'black' }}
                        />
                    </Stack>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
            <Box className="page-header">
                <Stack
                    pl={3}
                    pt={2}
                    direction="row"
                    alignItems="flex-end"
                    gap={2}
                    sx={{ width: 1 }}
                >
                    <Box
                        component="img"
                        src={report.logoUrl}
                        alt="Logo"
                        className="header-logo"
                        sx={{
                            maxHeight: 48,
                            maxWidth: 270,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Stack>
            </Box>

            <Box sx={{ bgcolor: '#f3f4f6', borderRadius: 2, px: 2, py: 1.25, mb: 2 }} mt={5}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: slideBrand, pb: 1.25 }}>
                    {localizeConfigText(slide.title, report.language) ?? 'Campaign Summary'}
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1.5 }}>
                    {report.title}
                    <Typography component="span" variant="h6" sx={{ fontWeight: 500 }}>
                        {report.referenceText}
                    </Typography>
                </Typography>

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid
                        item
                        xs={12}
                        md={slide.showCta ? 7 : 12}
                        sx={{
                            '@media print': slide.showCta
                                ? {
                                      width: '58.3333%',
                                      flexBasis: '58.3333%',
                                      maxWidth: '58.3333%',
                                  }
                                : {
                                      width: '100%',
                                      flexBasis: '100%',
                                      maxWidth: '100%',
                                  },
                        }}
                    >
                        <Stack spacing={0.75}>
                            {report.liveDates && (
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                        Campaign Live Dates
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {report.liveDates}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                    Current Report Dates
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {report.reportDates}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                    Campaign Number
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    #{campaignId}
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    {slide.showCta && (
                        <Grid
                            item
                            xs={12}
                            md={5}
                            sx={{
                                '@media print': {
                                    width: '41.6667%',
                                    flexBasis: '41.6667%',
                                    maxWidth: '41.6667%',
                                },
                            }}
                        >
                            <Box sx={{ backgroundColor: slideBrand, borderRadius: 2, p: 1.5 }}>
                                <Typography
                                    component="a"
                                    href={report.linkHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    display="flex"
                                    justifyContent="center"
                                    sx={{
                                        color: 'white',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: 14,
                                    }}
                                >
                                    Click here to view the full campaign performance on UniLED
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {slide.showPerformanceHeadlines !== false && (
                <Box sx={{ mt: 8 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: slideBrand, mb: 1.5 }}>
                        Performance Headlines
                    </Typography>

                    <Grid container spacing={1.5} className="metrics print-three">
                        {visibleSummaryCards.map(card => (
                            <Grid
                                item
                                xs={12}
                                md={4}
                                key={card.title}
                                sx={{
                                    '@media print': {
                                        width: '33.3333%',
                                        flexBasis: '33.3333%',
                                        maxWidth: '33.3333%',
                                    },
                                }}
                            >
                                <Box
                                    className="metric-card"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ color: report.brand }}
                                        display="flex"
                                        fontWeight={700}
                                        justifyContent="center"
                                    >
                                        {card.title}
                                    </Typography>
                                    {visibleSummaryCardRows.includes('performance') && (
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography sx={{ color: 'text.secondary', mt: 0.25 }}>
                                                Performance
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    color: perfColor(card.performance),
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {formatPercent(card.performance)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {visibleSummaryCardRows.includes('total') && (
                                        <SummaryMetricRow label="Total" value={card.total} />
                                    )}
                                    {visibleSummaryCardRows.includes('expected') && (
                                        <SummaryMetricRow label="Expected" value={card.expected} />
                                    )}
                                    {visibleSummaryCardRows.includes('variance') && (
                                        <SummaryMetricRow
                                            label="Variance"
                                            value={card.variance}
                                            color={card.varianceValue >= 0 ? '#158f43' : '#e11d48'}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            <Box className="page-footer">
                <Stack pl={2.2} direction="row" alignItems="flex-end" gap={2} sx={{ width: 1 }}>
                    <Box
                        component="img"
                        src={poweredByUniledLogo}
                        alt="Powered by UniLED"
                        sx={{
                            maxHeight: 48,
                            maxWidth: 270,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                    <Box sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'black' }} />
                </Stack>
            </Box>
        </Box>
    );
};

const SummaryMetricRow = ({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) => (
    <Box display="flex" justifyContent="space-between">
        <Typography sx={{ color: 'text.secondary', mt: 0.25 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 600, color }}>{value}</Typography>
    </Box>
);

const resolveTwoChartPane = (spec: ChartSpec, report: CustomReportResolvedData) => {
    if (spec.dataset === 'running_totals_impacts') {
        return {
            kind: 'lineRunningTotals' as const,
            title: localizeConfigText(spec.title, report.language) ?? spec.title,
            data: report.runningTotals.impacts,
            triplet:
                spec.showTriplet === false
                    ? undefined
                    : {
                          pct: report.summaryCards[0]?.performance ?? null,
                          target: report.summaryCards[0]?.expectedValue,
                          total: report.summaryCards[0]?.totalValue,
                          label: getMetricWord(spec.metric, report.language),
                          targetLabel: `Target ${getMetricWord(spec.metric, report.language)}`,
                          totalLabel: `Total ${getMetricWord(spec.metric, report.language)}`,
                      },
        };
    }

    if (spec.dataset === 'running_totals_plays') {
        return {
            kind: 'lineRunningTotals' as const,
            title: localizeConfigText(spec.title, report.language) ?? spec.title,
            data: report.runningTotals.plays,
            triplet:
                spec.showTriplet === false
                    ? undefined
                    : {
                          pct: report.summaryCards[1]?.performance ?? null,
                          target: report.summaryCards[1]?.expectedValue,
                          total: report.summaryCards[1]?.totalValue,
                          label: 'Plays',
                          targetLabel: 'Target Plays',
                          totalLabel: 'Total Plays',
                      },
        };
    }

    const datasetKey = getChartDatasetKey(spec);
    const rows = getRowsForDataset(report, datasetKey, {
        maxItems:
            spec.maxItems ?? (isChronologicalDataset(datasetKey) ? undefined : DEFAULT_MAX_ITEMS),
        includeZeroValues: spec.includeZeroValues,
    });
    const metricKeys = getMetricKeys(spec.metric);
    const metricWord = getMetricWord(spec.metric, report.language);

    return {
        kind: 'barTotalVsExpected' as const,
        title: localizeConfigText(spec.title, report.language) ?? spec.title,
        data: rows,
        xKey: 'name',
        totalKey: metricKeys.totalKey,
        expectedKey: metricKeys.expectedKey,
        names: {
            total: `Total ${metricWord}`,
            expected: `Expected ${metricWord}`,
        },
    };
};

const LinearTwoChartSlide: React.FC<{
    slide: Extract<CustomReportSlideConfig, { layout: 'linearTwoChart' }>;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);

    return (
        <TwoChartPage
            mainTitle={localizeConfigText(slide.mainTitle, report.language) ?? slide.mainTitle}
            brand={slideBrand}
            logoUrl={report.logoUrl}
            footer={{ title: report.title, campaignId, brand: footerBrand }}
            left={resolveTwoChartPane(slide.left, report)}
            right={resolveTwoChartPane(slide.right, report)}
            fmtCompact={fmtCompact}
            fmtInt={fmtInt}
            perfColor={perfColor}
            showValueLabels={Boolean(slide.left.showValueLabels || slide.right.showValueLabels)}
            xAxisTickFontSize={slide.left.fontSize ?? slide.right.fontSize ?? 9}
        />
    );
};

const buildInsightRows = (
    insight: InsightSpec,
    report: CustomReportResolvedData,
    use12hHourFormat?: boolean,
) => {
    if (insight.kind === 'customRows') {
        return insight.rows
            .map(row => buildCustomInsightRow(row, report, use12hHourFormat))
            .filter(Boolean);
    }

    const datasetKey = getInsightDatasetKey(insight);
    const rows = getRowsForDataset(report, datasetKey, {
        includeZeroValues: true,
    });
    const metricWord = getMetricWord(insight.metric, report.language);

    if (insight.kind === 'dayNightShare') {
        const total =
            rows.reduce((sum, row) => sum + getMetricValue(row, insight.metric).actual, 0) || 1;
        const daySum = rows
            .filter(row => {
                const hour = Number(row.name);
                return hour >= 5 && hour <= 22;
            })
            .reduce((sum, row) => sum + getMetricValue(row, insight.metric).actual, 0);
        const nightSum = Math.max(0, total - daySum);

        const use12h = resolveUse12hHourFormat(insight.hourFormat, use12hHourFormat);
        const dayRange = formatHourRange(5, 23, use12h);
        const nightRange = formatHourRange(23, 5, use12h);

        return [
            {
                label: (
                    <>
                        <strong style={{ color: report.brand }}>
                            {Math.round((daySum / total) * 100)}%
                        </strong>{' '}
                        of {metricWord} delivered between {dayRange}
                    </>
                ),
            },
            {
                label: (
                    <>
                        <strong style={{ color: report.brand }}>
                            {Math.round((nightSum / total) * 100)}%
                        </strong>{' '}
                        of {metricWord} delivered between {nightRange}
                    </>
                ),
            },
        ];
    }

    const limit = insight.limit ?? 6;
    const actualRows = rows.filter(row => getMetricValue(row, insight.metric).actual > 0);
    const sortedRows = isChronologicalDataset(datasetKey)
        ? actualRows
        : [...actualRows].sort(
              (a, b) =>
                  getMetricValue(b, insight.metric).actual -
                  getMetricValue(a, insight.metric).actual,
          );
    const total =
        sortedRows.reduce((sum, row) => sum + getMetricValue(row, insight.metric).actual, 0) || 1;
    const topRows = sortedRows.slice(0, limit);
    const topTotal = topRows.reduce(
        (sum, row) => sum + getMetricValue(row, insight.metric).actual,
        0,
    );
    const others = Math.max(0, total - topTotal);

    const preposition = insight.preposition ?? 'on';
    const rowsOut = topRows.map(row => ({
        label: (
            <>
                <strong style={{ color: report.brand }}>
                    {Math.round((getMetricValue(row, insight.metric).actual / total) * 100)}%
                </strong>{' '}
                of {metricWord} delivered {preposition} {row.name}
            </>
        ),
    }));

    if (!isChronologicalDataset(datasetKey) && others > 0) {
        rowsOut.push({
            label: (
                <>
                    <strong style={{ color: report.brand }}>
                        {Math.round((others / total) * 100)}%
                    </strong>{' '}
                    of {metricWord} delivered {preposition} {insight.othersLabel ?? 'Others'}
                </>
            ),
        });
    }

    return rowsOut;
};

const buildCustomInsightRow = (
    row: CustomInsightRowSpec,
    report: CustomReportResolvedData,
    use12hHourFormat?: boolean,
): { label: React.ReactNode; value?: React.ReactNode } | null => {
    if (row.kind === 'static') {
        return {
            label: localizeConfigNode(row.label, report.language),
            value: localizeConfigNode(row.value, report.language),
        };
    }

    if (row.kind === 'hourRangeShare') {
        const rows = getRowsForDataset(report, row.dataset, { includeZeroValues: true });
        const total =
            rows.reduce((sum, item) => sum + getMetricValue(item, row.metric).actual, 0) || 1;
        const rangeTotal = rows
            .filter(item => {
                const hour = Number(item.name);
                return hour >= row.startHour && hour <= row.endHour;
            })
            .reduce((sum, item) => sum + getMetricValue(item, row.metric).actual, 0);
        const metricWord = getMetricWord(row.metric, report.language);
        const percent = Math.round((rangeTotal / total) * 100);
        const use12h = resolveUse12hHourFormat(row.hourFormat, use12hHourFormat);
        const defaultLabel = `of ${metricWord} delivered between ${formatHourRange(
            row.startHour,
            row.endHour + 1,
            use12h,
        )}`;

        return {
            label: (
                <>
                    <strong style={{ color: report.brand }}>{percent}%</strong>{' '}
                    {localizeConfigText(row.label, report.language) ?? defaultLabel}
                </>
            ),
        };
    }

    const metricWord = getMetricWord(row.metric, report.language);
    const datasetKey = getInsightDatasetKey(row);
    const rows = getRowsForDataset(report, datasetKey, { includeZeroValues: true });
    const orderedRows =
        (row.kind === 'topShare' && row.sortBy === 'chronological') ||
        isChronologicalDataset(datasetKey)
            ? rows
            : [...rows].sort(
                  (a, b) =>
                      getMetricValue(b, row.metric).actual - getMetricValue(a, row.metric).actual,
              );
    const total =
        orderedRows.reduce((sum, item) => sum + getMetricValue(item, row.metric).actual, 0) || 1;

    if (row.kind === 'topShare') {
        const target = orderedRows[row.index];
        if (!target) return null;
        const percent = Math.round((getMetricValue(target, row.metric).actual / total) * 100);
        const defaultLabel = `of ${metricWord} delivered ${row.preposition ?? 'on'} ${target.name}`;

        return {
            label: (
                <>
                    <strong style={{ color: report.brand }}>{percent}%</strong>{' '}
                    {localizeConfigText(row.label, report.language) ?? defaultLabel}
                </>
            ),
        };
    }

    const topRows = orderedRows.slice(0, row.limit);
    const topTotal = topRows.reduce(
        (sum, item) => sum + getMetricValue(item, row.metric).actual,
        0,
    );
    const others = Math.max(0, total - topTotal);
    const percent = Math.round((others / total) * 100);
    const defaultLabel = `of ${metricWord} delivered ${row.preposition ?? 'on'} ${
        row.othersLabel ?? 'Others'
    }`;

    return {
        label: (
            <>
                <strong style={{ color: report.brand }}>{percent}%</strong>{' '}
                {localizeConfigText(row.label, report.language) ?? defaultLabel}
            </>
        ),
    };
};

const renderChart = (spec: ChartSpec, report: CustomReportResolvedData) => {
    const xAxisFontSize = spec.fontSize ?? 11;

    if (spec.dataset === 'running_totals_impacts' || spec.dataset === 'running_totals_plays') {
        const data =
            spec.dataset === 'running_totals_impacts'
                ? report.runningTotals.impacts
                : report.runningTotals.plays;
        const metricWord = getMetricWord(spec.metric, report.language);

        return (
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 16, right: 12, left: 12, bottom: 28 }}>
                    <CartesianGrid vertical={false} stroke="#E5E7EB" />
                    <XAxis
                        dataKey="label"
                        height={X_AXIS_HEIGHT}
                        interval={0}
                        tick={<AngledMultiLineTick fontSize={xAxisFontSize} />}
                    />
                    <YAxis tickFormatter={value => fmtCompact(Number(value))} width={65} />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="Total"
                        name={`Total ${metricWord}`}
                        stroke={report.brand}
                        strokeWidth={3}
                        dot={false}
                    />
                    {spec.showExpected !== false && (
                        <Line
                            type="monotone"
                            dataKey="Expected"
                            name={`Expected ${metricWord}`}
                            stroke="#94A3B8"
                            strokeWidth={2}
                            dot={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        );
    }

    const datasetKey = getChartDatasetKey(spec);
    const rows = getRowsForDataset(report, datasetKey, {
        maxItems:
            spec.maxItems ?? (isChronologicalDataset(datasetKey) ? undefined : DEFAULT_MAX_ITEMS),
        includeZeroValues: spec.includeZeroValues,
    });
    const metricKeys = getMetricKeys(spec.metric);
    const metricWord = getMetricWord(spec.metric, report.language);

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 16, right: 12, left: 12, bottom: 28 }}>
                <CartesianGrid vertical={false} stroke="#E5E7EB" />
                <XAxis
                    dataKey="name"
                    interval={0}
                    height={X_AXIS_HEIGHT}
                    tick={<AngledMultiLineTick fontSize={xAxisFontSize} />}
                />
                <YAxis tickFormatter={value => fmtCompact(Number(value))} width={70} />
                <Legend />
                <Bar
                    dataKey={metricKeys.totalKey}
                    name={`Total ${metricWord}`}
                    fill={report.brand}
                    radius={[6, 6, 0, 0]}
                >
                    {spec.showValueLabels && (
                        <LabelList
                            dataKey={metricKeys.totalKey}
                            position="top"
                            formatter={fmtCompact}
                            style={{ fill: report.brand }}
                        />
                    )}
                </Bar>
                {spec.showExpected !== false && (
                    <Bar
                        dataKey={metricKeys.expectedKey}
                        name={`Expected ${metricWord}`}
                        fill="#CBD5E1"
                        radius={[6, 6, 0, 0]}
                    >
                        {spec.showValueLabels && (
                            <LabelList
                                dataKey={metricKeys.expectedKey}
                                position="top"
                                formatter={fmtCompact}
                                style={{ fill: '#CBD5E1' }}
                            />
                        )}
                    </Bar>
                )}
            </BarChart>
        </ResponsiveContainer>
    );
};

const DspSplitChartInsightsSlide: React.FC<{
    slide: DspSplitChartInsightsSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
    use12hHourFormat?: boolean;
}> = ({ slide, report, campaignId, use12hHourFormat }) => {
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);

    return (
        <CustomSplitReportPage
            logoUrl={report.logoUrl}
            brand={slideBrand}
            footerBrand={footerBrand}
            campaignId={campaignId}
            campaignTitle={report.title}
            splitWidth={slide.layoutConfig?.splitWidth ?? '50%'}
        >
            <SplitChartWithInsights
                language={report.language}
                brand={slideBrand}
                heading={localizeConfigText(slide.heading, report.language) ?? slide.heading}
                metricWord={slide.metricWord ?? getMetricWord(slide.chart.metric, report.language)}
                chart={renderChart(slide.chart, report)}
                insights={buildInsightRows(slide.insights, report, use12hHourFormat)}
                chartHeight={slide.chartHeight}
                leftPillText={localizeConfigText(slide.leftPillText, report.language)}
                rightPillText={localizeConfigText(slide.rightPillText, report.language)}
            />
        </CustomSplitReportPage>
    );
};

const SingleChartSlide: React.FC<{
    slide: SingleChartSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);

    return (
        <SplitReportPage
            logoUrl={report.logoUrl}
            brand={slideBrand}
            footerBrand={footerBrand}
            campaignId={campaignId}
            campaignTitle={report.title}
        >
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 3, color: '#111' }}>
                {localizeConfigText(slide.mainTitle, report.language)}
            </Typography>
            <Box sx={{ height: 510 }}>
                <ChartCard
                    title={
                        localizeConfigText(slide.chart.title, report.language) ?? slide.chart.title
                    }
                    brand={slideBrand}
                >
                    {renderChart(slide.chart, report)}
                </ChartCard>
            </Box>
        </SplitReportPage>
    );
};

const PerformanceBoxesSlide: React.FC<{
    slide: PerformanceBoxesSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);
    const rows = getRowsForDataset(report, slide.dataset, {
        maxItems: slide.maxItems ?? 6,
    });
    const sortedRows = [...rows].sort((a, b) => {
        if (slide.sortBy === 'actual') {
            return getMetricValue(b, slide.metric).actual - getMetricValue(a, slide.metric).actual;
        }
        if (slide.sortBy === 'variance') {
            return (
                getMetricValue(b, slide.metric).variance - getMetricValue(a, slide.metric).variance
            );
        }
        return (
            getMetricValue(b, slide.metric).performance -
            getMetricValue(a, slide.metric).performance
        );
    });

    return (
        <SplitReportPage
            logoUrl={report.logoUrl}
            brand={slideBrand}
            footerBrand={footerBrand}
            campaignId={campaignId}
            campaignTitle={report.title}
        >
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: '#111' }}>
                {localizeConfigText(slide.mainTitle, report.language)}
            </Typography>
            <Box
                sx={{
                    bgcolor: slideBrand,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: 1.5,
                    px: 3,
                    py: 1.25,
                    mb: 3,
                    fontWeight: 900,
                }}
            >
                {localizeConfigText(slide.title, report.language)}
            </Box>

            <Grid
                container
                spacing={2}
                className={(slide.columns ?? 3) === 4 ? 'print-four' : 'print-three'}
            >
                {sortedRows.map(row => {
                    const value = getMetricValue(row, slide.metric);
                    return (
                        <Grid
                            item
                            xs={12}
                            md={12 / (slide.columns ?? 3)}
                            key={row.name}
                            sx={{
                                '@media print': {
                                    width: `${100 / (slide.columns ?? 3)}%`,
                                    flexBasis: `${100 / (slide.columns ?? 3)}%`,
                                    maxWidth: `${100 / (slide.columns ?? 3)}%`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 2,
                                    height: '100%',
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                                    {row.name}
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{ fontWeight: 900, color: perfColor(value.performance) }}
                                >
                                    {value.performanceLabel}
                                </Typography>
                                <SummaryMetricRow label="Total" value={fmtInt(value.actual)} />
                                <SummaryMetricRow label="Expected" value={fmtInt(value.expected)} />
                                <SummaryMetricRow
                                    label="Variance"
                                    value={`${value.variance > 0 ? '+' : ''}${fmtInt(
                                        value.variance,
                                    )}`}
                                    color={value.variance >= 0 ? '#158f43' : '#e11d48'}
                                />
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </SplitReportPage>
    );
};

function ImpactTable({
    rows,
    columns,
    brand,
    positive,
    emptyStateText,
}: {
    rows: MultiplierRow[];
    columns: Array<ImpactMultiplierColumnConfig & { label: string }>;
    brand: string;
    positive: boolean;
    emptyStateText?: string;
}) {
    const headerSx = {
        bgcolor: brand,
        color: '#fff',
        fontWeight: 800,
        fontSize: 11,
        whiteSpace: 'nowrap' as const,
    };
    const badgeBg = positive ? '#AEDDAB' : '#F4B084';

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell key={col.id as string} align="center" sx={headerSx}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow
                            key={`${row.display_owner}-${row.display_format}-${row.dsp_impressions}-${row.variance_percent}`}
                        >
                            {columns.map(col => {
                                const isPercent = col.id === 'variance_percent';
                                const rawValue = row[col.id as keyof MultiplierRow];
                                const value = formatImpactMultiplierCell(col.id, rawValue);

                                return (
                                    <TableCell
                                        key={String(col.id)}
                                        align="center"
                                        sx={{
                                            fontSize: 10,
                                            border: '1px solid black',
                                            ...(isPercent
                                                ? {
                                                      bgcolor: badgeBg,
                                                      fontWeight: 700,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {value}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                    {!rows.length && (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                            >
                                {emptyStateText ?? 'No inventory applicable here.'}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const formatImpactMultiplierCell = (id: ImpactMultiplierColumnId, value: unknown) => {
    switch (id) {
        case 'display_owner':
        case 'display_format':
            return String(value ?? '');
        case 'sum_of_play':
        case 'dsp_impressions':
        case 'geopath_impressions':
            return fmtInt(value as number | undefined);
        case 'variance_percent':
            return fmtPct(Number(value ?? 0));
        default:
            return fmt2(value as number | undefined);
    }
};

const buildImpactMultiplierColumns = (
    source: 'geo' | 'nongeo',
    language: 'GB' | 'US',
    routeTranslation: boolean,
    columns?: ImpactMultiplierColumnConfig[],
) => {
    const routeWord = routeTranslation ? 'Route' : 'Geopath';
    const defaultGeo = [
        { id: 'display_owner', label: 'Display Owner' },
        { id: 'display_format', label: 'Display Format' },
        { id: 'sum_of_play', label: '# Plays' },
        { id: 'dsp_impressions', label: language === 'GB' ? 'DSP Impacts' : 'DSP Impressions' },
        {
            id: 'geopath_impressions',
            label: language === 'GB' ? `${routeWord} Impacts` : `${routeWord} Impressions`,
        },
        { id: 'variance', label: 'VAR' },
        { id: 'dsp_avg_impressions', label: 'DSP IM' },
        { id: 'geopath_avg_impressions', label: routeTranslation ? 'Route IM' : 'Geopath IM' },
        { id: 'average_im', label: 'Average Format IM' },
        { id: 'variance_percent', label: '% VAR' },
    ] satisfies ImpactMultiplierColumnConfig[];

    const defaultNonGeo = [
        { id: 'display_owner', label: 'Display Owner' },
        { id: 'display_format', label: 'Display Format' },
        { id: 'sum_of_play', label: '# Plays' },
        { id: 'dsp_impressions', label: language === 'GB' ? 'DSP Impacts' : 'DSP Impressions' },
        { id: 'dsp_avg_impressions', label: 'DSP IM' },
        { id: 'average_im', label: 'Average IM' },
        { id: 'variance', label: 'IM VAR' },
        { id: 'variance_percent', label: '% VAR' },
    ] satisfies ImpactMultiplierColumnConfig[];

    const resolved = columns ?? (source === 'geo' ? defaultGeo : defaultNonGeo);
    return resolved.map(column => ({
        ...column,
        label:
            column.label ??
            (source === 'geo'
                ? defaultGeo.find(item => item.id === column.id)?.label
                : defaultNonGeo.find(item => item.id === column.id)?.label) ??
            String(column.id),
    }));
};

const ImpactMultiplierSlide: React.FC<{
    slide: ImpactMultiplierSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const preset = slide.preset ?? slide.variant ?? 'maintainGrow';
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);
    const positive = preset === 'maintainGrow';
    const metricWord = report.language === 'GB' ? 'Impact' : 'Impression';
    const routeWord = report.routeTranslation ? 'Route' : 'Geopath';
    const maxRows = slide.maxRows ?? 5;
    const defaultSections: ImpactMultiplierSectionConfig[] = [
        {
            source: 'geo',
            title: `${routeWord} Audited Inventory`,
            emphasisWord: positive ? 'HIGHER' : 'LOWER',
        },
        {
            source: 'nongeo',
            title: `Non-${routeWord} Audited Inventory`,
            emphasisWord: positive ? 'HIGHER' : 'LOWER',
        },
    ];
    const sections = slide.sections ?? defaultSections;

    return (
        <SplitReportPage
            logoUrl={report.logoUrl}
            brand={slideBrand}
            footerBrand={footerBrand}
            campaignId={campaignId}
            campaignTitle={report.title}
        >
            <Typography variant="h3" sx={{ fontWeight: 900, mb: 2 }}>
                {localizeConfigText(slide.title, report.language) ??
                    (positive
                        ? `${metricWord} | Optimizations to Maintain & Grow`
                        : `${metricWord} Analysis | Optimizations to Review & Consider`)}
            </Typography>
            <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 20 }}>
                {localizeConfigText(slide.subtitle, report.language) ??
                    `Top ${maxRows} (${
                        report.language === 'GB' ? 'Impact Delivery' : 'Impression Delivery'
                    })`}
            </Typography>

            {sections.map((section, index) => {
                const rows =
                    section.source === 'geo'
                        ? pickTopBottom(report.multipliers.geoRows, positive, maxRows)
                        : pickTopBottom(report.multipliers.nonGeoRows, positive, maxRows);
                const columns = buildImpactMultiplierColumns(
                    section.source,
                    report.language,
                    report.routeTranslation,
                    section.columns,
                );
                const defaultBody =
                    section.source === 'geo'
                        ? `The Average ${routeWord} DSP IM is `
                        : 'The Average Format DSP IM is ';

                return (
                    <Box
                        key={`${section.source}-${section.title}`}
                        sx={{ mt: index === 0 ? 0 : 4 }}
                    >
                        <Typography sx={{ mb: 1.5 }}>
                            <strong>{localizeConfigText(section.title, report.language)}:</strong>{' '}
                            {defaultBody}
                            <strong style={{ color: positive ? slideBrand : '#d32f2f' }}>
                                {section.emphasisWord ?? (positive ? 'HIGHER' : 'LOWER')}
                            </strong>{' '}
                            than the DSP IM for the same format type
                            {section.source === 'geo' ? ' *' : ''}
                        </Typography>
                        <ImpactTable
                            rows={rows}
                            columns={columns}
                            positive={positive}
                            brand={slideBrand}
                            emptyStateText={localizeConfigText(
                                section.emptyStateText,
                                report.language,
                            )}
                        />
                    </Box>
                );
            })}
        </SplitReportPage>
    );
};

const getMetricValue = (row: SummaryBarRow, metric: CustomChartMetric) => {
    const actual = metric === 'impacts' ? row.impacts_total : row.plays_total;
    const expected = metric === 'impacts' ? row.impacts_expected : row.plays_expected;
    const variance = actual - expected;
    const performance = expected > 0 ? (actual / expected) * 100 : 0;

    return {
        actual,
        expected,
        variance,
        performance,
        performanceLabel: expected > 0 ? `${Math.round(performance)}%` : '0%',
    };
};

const ThankYouSlide: React.FC<{
    slide: ThankYouSlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const preset = slide.preset ?? slide.variant ?? 'linear';
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);

    if (preset === 'dsp') {
        return (
            <Box className="page">
                <Box className="thankyou-center">
                    <Box>
                        <Typography sx={{ color: 'black', fontSize: '90px', fontWeight: 900 }}>
                            {localizeConfigText(slide.title, report.language) ?? 'Thank you'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: slideBrand,
                            height: '20px',
                            borderRadius: '2px',
                            width: 500,
                        }}
                    />
                    {slide.subtitle && (
                        <Typography sx={{ color: '#374151', maxWidth: 900, textAlign: 'center' }}>
                            {localizeConfigText(slide.subtitle, report.language)}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={3} pt={2}>
                        <Box
                            component="img"
                            src={uniledLogo}
                            alt="UniLED"
                            className="final-logo"
                            sx={{ width: 150, objectFit: 'contain', display: 'block' }}
                        />
                        <Box
                            sx={{
                                height: 48,
                                borderLeft: '1px solid',
                                borderColor: 'rgb(124,124,124)',
                            }}
                        />
                        <Box
                            component="img"
                            src={report.logoUrl}
                            alt="Partner"
                            className="final-logo"
                            sx={{ width: 150, objectFit: 'contain', display: 'block' }}
                        />
                    </Stack>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
            <Box className="page-header" sx={{ visibility: 'hidden' }}>
                <Box />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: { xs: 2, md: 6 },
                    minHeight: 'calc(100% - 0px)',
                }}
            >
                <Box>
                    <Typography
                        variant="h1"
                        className="final-title"
                        sx={{
                            fontWeight: 900,
                            lineHeight: 0.95,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: 45, md: 100 },
                        }}
                    >
                        {localizeConfigText(slide.title, report.language) ?? 'Thank you'}
                    </Typography>

                    <Box
                        className="final-bar"
                        sx={{
                            mt: 2,
                            mb: 3,
                            height: 10,
                            width: { xs: 240, md: 520 },
                            bgcolor: slideBrand,
                            borderRadius: 1.5,
                        }}
                    />

                    {slide.subtitle && (
                        <Typography sx={{ color: '#374151', maxWidth: 900, mb: 3 }}>
                            {localizeConfigText(slide.subtitle, report.language)}
                        </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={3}>
                        <Box
                            component="img"
                            src={uniledLogo}
                            alt="UniLED"
                            className="final-logo"
                            sx={{ height: 36, objectFit: 'contain', display: 'block' }}
                        />
                        <Box sx={{ height: 28, borderLeft: '1px solid', borderColor: 'divider' }} />
                        <Box
                            component="img"
                            src={report.logoUrl}
                            alt="Partner"
                            className="final-logo"
                            sx={{ height: 36, objectFit: 'contain', display: 'block' }}
                        />
                    </Stack>
                </Box>
            </Box>

            <Box className="page-footer" sx={{ visibility: 'hidden' }}>
                <Box />
            </Box>
        </Box>
    );
};

const GlossarySlide: React.FC<{
    slide: GlossarySlideConfig;
    report: CustomReportResolvedData;
    campaignId: number;
}> = ({ slide, report, campaignId }) => {
    const preset = slide.preset ?? slide.variant ?? 'linear';
    const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
    const footerBrand = resolveFooterBrand(report, slide.layoutConfig);
    const metricWordPlural = report.language === 'GB' ? 'Impacts' : 'Impressions';
    const metricWordSingular = report.language === 'GB' ? 'Impact' : 'Impression';

    if (preset === 'dspIntro') {
        return (
            <CustomSplitReportPage
                logoUrl={report.logoUrl}
                brand={slideBrand}
                footerBrand={footerBrand}
                campaignId={campaignId}
                campaignTitle={report.title}
                splitWidth={slide.layoutConfig?.splitWidth ?? '50%'}
            >
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 6, pl: 1 }}>
                    Glossary
                </Typography>

                <Box sx={{ maxWidth: 'calc(var(--split-w) - var(--pad-x))', pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: slideBrand, fontSize: 28, mb: 1.5 }}>
                        Geopath Audited Analysis
                    </Typography>
                    <Typography sx={{ lineHeight: 1.45, mb: 6 }}>
                        An analysis of Geopath Audited Units.
                        <br />
                        Comparing DSP {metricWordSingular}s vs Geopath {metricWordSingular}s for the
                        campaign.
                    </Typography>

                    <Typography sx={{ fontWeight: 900, color: slideBrand, fontSize: 28, mb: 1.5 }}>
                        Non-Geopath Audited Analysis
                    </Typography>
                    <Typography sx={{ lineHeight: 1.45 }}>
                        An analysis of Non-Geopath Audited Units.
                        <br />
                        Comparing Format {metricWordSingular}s against the Average Format{' '}
                        {metricWordSingular}s for the campaign.
                    </Typography>
                </Box>
            </CustomSplitReportPage>
        );
    }

    if (preset === 'dspCover') {
        return (
            <CustomSplitReportPage
                logoUrl={report.logoUrl}
                brand={slideBrand}
                footerBrand={footerBrand}
                campaignId={campaignId}
                campaignTitle={report.title}
                splitWidth={slide.layoutConfig?.splitWidth ?? '50%'}
            >
                <Box sx={{ maxWidth: 'calc(var(--split-w))' }}>
                    <Box sx={{ mt: 2, pl: 1 }}>
                        <Typography sx={{ color: 'black', fontSize: '72px', fontWeight: '900' }}>
                            Glossary of
                            <br />
                            Terms
                        </Typography>
                        <Box
                            className="cover-underline"
                            sx={{
                                bgcolor: slideBrand,
                                mt: 2,
                                height: 10,
                                width: 240,
                                borderRadius: 1.5,
                            }}
                        />
                    </Box>
                </Box>
            </CustomSplitReportPage>
        );
    }

    if (preset === 'dspTerms') {
        const metricWordSingular = report.language === 'GB' ? 'Impact' : 'Impression';
        const dspItems = [
            {
                term: 'DSP',
                definition:
                    'The Demand Side Platform where campaign delivery data and impression data are recorded.',
            },
            {
                term: 'SSP',
                definition:
                    'The Supply Side Platform representing the media owner or inventory source.',
            },
            {
                term: 'Play Data',
                definition:
                    'The count of ad plays recorded across the campaign inventory during the report period.',
            },
            {
                term: 'DSP Play Data',
                definition: 'Play data reported directly from the DSP or buying platform.',
            },
            {
                term: 'SSP Play Data',
                definition: 'Play data reported directly from the SSP or media owner platform.',
            },
            {
                term: 'Vendor Player Data',
                definition:
                    'Play data collected from the screen player or device-level reporting source.',
            },
        ];

        return (
            <CustomSplitReportPage
                logoUrl={report.logoUrl}
                brand={report.brand}
                footerBrand={footerBrand}
                campaignId={campaignId}
                campaignTitle={report.title}
            >
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 6, pl: 1 }}>
                    Glossary
                </Typography>
                <Grid container spacing={6} className="glossary-grid">
                    <Grid item xs={12} md={6}>
                        {dspItems.map(item => (
                            <Box key={item.term} sx={{ mb: 3 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 900, color: report.brand, mb: 1 }}
                                >
                                    {localizeConfigText(item.term, report.language)}
                                </Typography>
                                <Typography>
                                    {localizeConfigText(item.definition, report.language)}
                                </Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item xs={12} md={6} className="glossary-right" sx={{ pl: { md: 4 } }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, color: report.brand, mb: 1 }}
                        >
                            {metricWordSingular} Multiplier (IM)
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            The {metricWordSingular} multiplier is a DOOH-specific multiplication
                            index applied to each play on every DOOH screen to estimate the number
                            of {metricWordSingular}s delivered in a single ad play, at any given
                            day/hour.
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            Unlike most other digital advertising channels which operate on a
                            one-to-one basis (meaning one play equals one {metricWordSingular}),
                            DOOH is a one-to-many medium, meaning one play not only equals multiple{' '}
                            {metricWordSingular}s but the number of {metricWordSingular}s usually
                            differs depending on the unit, day, hour, and many other factors in the
                            OOH space. Because each {metricWordSingular} can reach multiple viewers,
                            a multiplier is needed to more accurately estimate how many viewers saw
                            the ad.
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            In the advancing world of programmatic DOOH, the {metricWordSingular}{' '}
                            multiplier was introduced to account for the broader reach of any given
                            play on a DOOH screen and allows DOOH to align itself with the{' '}
                            {metricWordSingular}-based buying model while remaining true to the
                            greatest strength of the medium - its capacity for mass reach.
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, color: report.brand, mt: 4, mb: 1 }}
                        >
                            Geopath {metricWordSingular}
                        </Typography>
                        <Typography sx={{ lineHeight: 1.45 }}>
                            Geopath generates the industry standard audience measurements for out of
                            home media. Geopath is OOH&apos;s governing measurement body to
                            establish and supervise practices for the collection, recording,
                            authentication, and verification of data related to the measurement of
                            OOH audiences.
                        </Typography>
                    </Grid>
                </Grid>
            </CustomSplitReportPage>
        );
    }

    if (preset === 'linear') {
        const items = slide.items ?? [
            {
                term: 'In Schedule',
                definition: `Plays or ${metricWordPlural} on booked units running during the booked times`,
            },
            {
                term: 'Out of Schedule',
                definition: `Plays or ${metricWordPlural} on booked units running outside of booked times`,
            },
            {
                term: 'Unbooked',
                definition: `Plays or ${metricWordPlural} on a unit that was not in the original booked plan`,
            },
        ];

        return (
            <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <Box className="page-header">
                    <Stack
                        pl={3}
                        pt={2}
                        direction="row"
                        alignItems="flex-end"
                        gap={2}
                        sx={{ width: 1 }}
                    >
                        <Box
                            component="img"
                            src={report.logoUrl}
                            alt="Logo"
                            className="header-logo"
                            sx={{
                                maxHeight: 48,
                                maxWidth: 270,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    </Stack>
                </Box>

                <Box sx={{ pt: 4, pl: { xs: 3, md: 8 }, pr: { xs: 2, md: 6 } }}>
                    <Box
                        sx={{
                            display: 'inline-block',
                            bgcolor: report.brand,
                            borderRadius: 2,
                            px: 2.25,
                            py: 0.75,
                            mb: 5,
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{ fontWeight: 900, color: '#fff', letterSpacing: 0.2 }}
                        >
                            {localizeConfigText(slide.title, report.language) ?? 'Glossary'}
                        </Typography>
                    </Box>
                    <Box
                        className="glossary-grid"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'max-content 1.25ch 1fr' },
                            columnGap: { xs: 1.5, md: 3 },
                            rowGap: { xs: 2.5, md: 3 },
                            alignItems: 'start',
                        }}
                    >
                        {items.map(item => (
                            <React.Fragment key={item.term}>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {localizeConfigText(item.term, report.language)}
                                </Typography>
                                <Typography sx={{ textAlign: 'center' }}>=</Typography>
                                <Typography>
                                    {localizeConfigText(item.definition, report.language)}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                <Box className="page-footer">
                    <Stack pl={2.2} direction="row" alignItems="flex-end" gap={2} sx={{ width: 1 }}>
                        <Box
                            component="img"
                            src={poweredByUniledLogo}
                            alt="Powered by UniLED"
                            sx={{
                                maxHeight: 48,
                                maxWidth: 270,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'black' }}
                        />
                    </Stack>
                </Box>
            </Box>
        );
    }

    const items = slide.items ?? [];
    return (
        <SplitReportPage
            logoUrl={report.logoUrl}
            brand={report.brand}
            campaignId={campaignId}
            campaignTitle={report.title}
        >
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 3, color: '#111' }}>
                {localizeConfigText(slide.title, report.language) ?? 'Glossary'}
            </Typography>
            <Grid container spacing={2}>
                {items.map(item => (
                    <Grid item xs={12} md={6} key={item.term}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                height: '100%',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 800, color: report.brand, mb: 1 }}
                            >
                                {localizeConfigText(item.term, report.language)}
                            </Typography>
                            <Typography sx={{ color: '#374151' }}>
                                {localizeConfigText(item.definition, report.language)}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </SplitReportPage>
    );
};

const slideRenderers: Record<
    CustomReportSlideConfig['layout'],
    React.FC<{
        slide: any;
        report: CustomReportResolvedData;
        campaignId: number;
        use12hHourFormat?: boolean;
    }>
> = {
    summaryCover: SummaryCoverSlide,
    linearTwoChart: LinearTwoChartSlide,
    dspSplitChartInsights: DspSplitChartInsightsSlide,
    singleChart: SingleChartSlide,
    performanceBoxes: PerformanceBoxesSlide,
    impactMultiplier: ImpactMultiplierSlide,
    oneMap: ({ slide, report, campaignId }) => {
        const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
        const footerBrand = resolveFooterBrand(report, slide.layoutConfig);

        return (
            <OneMapPage
                mainTitle={localizeConfigText(slide.mainTitle, report.language) ?? slide.mainTitle}
                brand={slideBrand}
                logoUrl={report.logoUrl}
                footer={{ title: report.title, campaignId, brand: footerBrand }}
                map={{
                    title: localizeConfigText(slide.map.title, report.language) ?? slide.map.title,
                    data: report.mapData as any,
                    mode: slide.map.mode,
                }}
            />
        );
    },
    twoMap: ({ slide, report, campaignId }) => {
        const slideBrand = resolveSlideBrand(report, slide.layoutConfig);
        const footerBrand = resolveFooterBrand(report, slide.layoutConfig);

        return (
            <TwoMapPage
                mainTitle={localizeConfigText(slide.mainTitle, report.language) ?? slide.mainTitle}
                brand={slideBrand}
                logoUrl={report.logoUrl}
                footer={{ title: report.title, campaignId, brand: footerBrand }}
                left={{
                    title:
                        localizeConfigText(slide.left.title, report.language) ?? slide.left.title,
                    data: report.mapData as any,
                    mode: slide.left.mode,
                }}
                right={{
                    title:
                        localizeConfigText(slide.right.title, report.language) ?? slide.right.title,
                    data: report.mapData as any,
                    mode: slide.right.mode,
                }}
            />
        );
    },
    thankYou: ThankYouSlide,
    glossary: GlossarySlide,
};

export const customReportStyles = `
  @page {
    size: A4 landscape;
    margin: 0;
  }

  :root {
    --page-w: 297mm;
    --page-h: 210mm;
    --header-h: 84px;
    --footer-h: 64px;
    --pad-x: 24px;
    --pad-top-extra: 16px;
    --pad-bot-extra: 16px;
    --split-w: 50%;
    --footer-bar-h: 18px;
  }

  @media screen {
    .page {
      width: 1200px;
      min-height: 700px;
      margin: 16px auto;
      background: #fff;
      position: relative;
      box-sizing: border-box;
      padding: calc(var(--header-h) + var(--pad-top-extra)) var(--pad-x) calc(var(--footer-h) + var(--pad-bot-extra));
    }
    .page--grey {
      background: #f3f4f6 !important;
    }
    .page + .page { margin-top: 24px; }
    .page .page-header { position: absolute; top: 0; left: 0; right: 0; height: var(--header-h); }
    .page .page-footer { position: absolute; bottom: 0; left: 0; right: 0; height: var(--footer-h); }
    .header-logo {
      display: block;
      max-height: 44px;
      max-width: 240px;
      object-fit: contain;
      margin-top: 4px;
    }
    .page .center-divider {
      position: absolute;
      left: 50%;
      transform: translateX(-0.5px);
      top: calc(var(--header-h) + 56px);
      bottom: calc(var(--footer-h) + 100px);
      border-left: 1px solid #d9d9d9;
      pointer-events: none;
      z-index: 2;
    }
    .three-col > .MuiGrid-item { width: 33.3333% !important; flex-basis: 33.3333% !important; }
    .two-col > .MuiGrid-item { width: 50% !important; flex-basis: 50% !important; }
    .summary-row {
      display: flex !important;
      align-items: flex-start !important;
    }
    .summary-row > .summary-left {
      width: 65% !important;
      flex-basis: 65% !important;
    }
    .summary-row > .summary-right {
      width: 35% !important;
      flex-basis: 35% !important;
    }
    .summary-cta { margin-top: 0 !important; }
    .split-bg-left {
      background: linear-gradient(to right, #f3f4f6 0 var(--split-w), #ffffff var(--split-w) 100%);
    }
    .split-bg-right {
      background: linear-gradient(to right, #ffffff 0 var(--split-w), #f3f4f6 var(--split-w) 100%);
    }
    .split-grid {
      display: grid;
      grid-template-columns: var(--split-w) calc(100% - var(--split-w));
      column-gap: 28px;
      align-items: start;
    }
    .chartArea .recharts-responsive-container,
    .chartArea .recharts-wrapper {
      width: 100% !important;
      max-width: 100% !important;
      overflow: hidden;
    }
    .page--maps {
      --footer-h: 84px;
    }
    .page--maps .page-footer {
      z-index: 1200;
    }
  }

  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      width: var(--page-w) !important;
      min-height: var(--page-h) !important;
      height: var(--page-h) !important;
      page-break-after: always;
      background: #fff;
      position: relative;
      box-sizing: border-box;
      padding: calc(var(--header-h) + var(--pad-top-extra)) var(--pad-x) calc(var(--footer-h) + var(--pad-bot-extra));
      overflow: hidden;
    }
    .page--grey {
      background: #f3f4f6 !important;
    }
    .page:last-child { page-break-after: auto; }
    .page .page-header { position: absolute; top: 0; left: 0; right: 0; height: var(--header-h); }
    .page .page-footer { position: absolute; bottom: 0; left: 0; right: 0; height: var(--footer-h); }
    .header-logo {
      max-height: 30px !important;
      max-width: 180px !important;
      object-fit: contain !important;
    }
    .page .center-divider {
      position: absolute;
      left: 50%;
      transform: translateX(-0.5px);
      top: calc(var(--header-h) + 50px);
      bottom: calc(var(--footer-h) + 100px);
      border-left: 1px solid #d9d9d9;
      pointer-events: none;
      z-index: 2;
    }
    .three-col > .MuiGrid-item,
    .print-three > .MuiGrid-item {
      width: 33.3333% !important;
      flex-basis: 33.3333% !important;
    }
    .print-four > .MuiGrid-item {
      width: 25% !important;
      flex-basis: 25% !important;
    }
    .two-col > .MuiGrid-item {
      width: 50% !important;
      flex-basis: 50% !important;
    }
    /* DSP glossary: MUI Grid stays as two columns when printing */
    .glossary-grid.MuiGrid-container > .MuiGrid-item {
      width: 50% !important;
      flex-basis: 50% !important;
      max-width: 50% !important;
    }
    /* Linear glossary: keep the term | = | definition columns when printing */
    .glossary-grid:not(.MuiGrid-container) {
      display: grid !important;
      grid-template-columns: max-content 1.25ch 1fr !important;
      column-gap: 24px !important;
      row-gap: 24px !important;
      align-items: start !important;
    }
    .summary-row {
      display: flex !important;
      align-items: flex-start !important;
    }
    .summary-row > .summary-left {
      width: 65% !important;
      flex-basis: 65% !important;
    }
    .summary-row > .summary-right {
      width: 35% !important;
      flex-basis: 35% !important;
    }
    .summary-cta { margin-top: 0 !important; }
    .metrics .metric-card { padding: 10px !important; }
    .metrics .metric-title { font-size: 18px !important; line-height: 1.15 !important; margin-bottom: 2px !important; }
    .metrics .metric-perf-label { font-size: 12px !important; }
    .metrics .metric-perf-value { font-size: 18px !important; line-height: 1.1 !important; }
    .metrics .metric-row-label,
    .metrics .metric-row-value { font-size: 12px !important; }
    .split-bg-left {
      background: linear-gradient(to right, #f3f4f6 0 var(--split-w), #ffffff var(--split-w) 100%) !important;
    }
    .split-bg-right {
      background: linear-gradient(to right, #ffffff 0 var(--split-w), #f3f4f6 var(--split-w) 100%) !important;
    }
    .split-grid {
      display: grid !important;
      grid-template-columns: var(--split-w) calc(100% - var(--split-w)) !important;
      column-gap: 20px !important;
      align-items: start !important;
    }
    .chartArea {
      min-width: 0 !important;
      overflow: hidden !important;
      padding-right: 0 !important;
    }
    .chartArea .recharts-responsive-container,
    .chartArea .recharts-wrapper {
      width: 100% !important;
      max-width: 100% !important;
      overflow: hidden !important;
    }
    .page--maps {
      --footer-h: 84px;
    }
    .page--maps .page-footer {
      z-index: 1200;
    }
  }
`;

export const renderCustomSlides = ({
    report,
    slides,
    campaignId,
    use12hHourFormat,
}: {
    report: CustomReportResolvedData | null;
    slides: CustomReportSlideConfig[];
    campaignId: number;
    use12hHourFormat?: boolean;
}) => {
    if (!report) {
        return (
            <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return slides
        .filter(slide => slide.enabled)
        .map(slide => {
            const Slide = slideRenderers[slide.layout];
            return (
                <Slide
                    key={slide.id}
                    slide={slide}
                    report={report}
                    campaignId={campaignId}
                    use12hHourFormat={use12hHourFormat}
                />
            );
        });
};
